<template>
  <div
    class="w-full cursor-pointer px-4 py-2 flex tablet-h:rounded-[10px] tablet-h:p-1.5 tablet-h:pr-2.5"
    :class="classes"
    @click="handleClick"
  >
    <div class="relative mr-[9px] size-11 shrink-0 p-0.5">
      <div class="size-full rounded-[4px] bg-gray-200/30 p-0.5">
        <Thumbnail
          :type="fileTypeToThumbnail(file?.type)"
          :src="notification.primaryIcon.imageUrl ?? undefined"
        />
      </div>
      <AlertCircleIcon class="absolute bottom-0 right-0 z-2 size-[18px]" />
    </div>
    <div
      class="text-[14px] leading-[19px] text-gray-800 -mt-1 flex flex-col justify-between"
    >
      <component :is="copy" class="mb-[3px]" />
      <div class="text-[12px] leading-[18px] opacity-60">
        {{ formatDistanceToNowStrict(notification.timestamp) }} ago
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NotificationType,
  type Notification,
} from '@/shared/graphql/legacy/generated'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { toFlaggedInfringement } from '@/shared/dialogs/infringement/FlaggedMatch/fromNotification'
import { AlertCircleIcon } from '@dacr/icons'
import { Thumbnail } from '@dacr/library'
import { formatDistanceToNowStrict } from 'date-fns'
import { computed, h } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  notification: Notification
}>()

const router = useRouter()

const classes = computed(() => ({
  'tablet-h:hover:bg-[#FAFAFA]': props.notification.read,
  'bg-green-700/10': !props.notification.read,
}))

const matchMetadata = computed(() => {
  const value = props.notification.metadata

  return value?.__typename === 'InfringementMatchMetadata' ? value : null
})

const reportMetadata = computed(() => {
  const value = props.notification.metadata

  return value?.__typename === 'ReportMetadata' ? value : null
})

const file = computed(
  () =>
    matchMetadata.value?.infringingFile ??
    reportMetadata.value?.registeredFile ??
    null
)

const handleClick = () => {
  if (matchMetadata.value) {
    openDialog(Dialog.FlaggedMatch, {
      props: {
        infringement: toFlaggedInfringement(
          matchMetadata.value,
          props.notification.timestamp
        ),
      },
    })
    router.push('/infringement')
    return
  }

  if (reportMetadata.value) {
    router.push(`/infringement/report/${reportMetadata.value.id}`)
  }
}

const nameClass = 'font-medium break-all'

const copy = computed(() => {
  if (
    props.notification.type === NotificationType.InfringementMatchCreated &&
    matchMetadata.value
  ) {
    return h('div', [
      'Potential infringements of ',
      h(
        'span',
        { class: nameClass },
        matchMetadata.value.infringingFile.fileName
      ),
      ' were found.',
    ])
  }

  if (
    props.notification.type === NotificationType.ReportCreated &&
    reportMetadata.value
  ) {
    return h('div', [
      'Your infringement report for ',
      h(
        'span',
        { class: nameClass },
        reportMetadata.value.registeredFile.fileName
      ),
      ' is ready.',
    ])
  }

  return null
})
</script>
